import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Apple, Monitor, Terminal, ShieldCheck, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";

const SHOTS = [
  ["session", "Prise en main d'un poste Windows"],
  ["access", "Tous vos postes, classés par client"],
  ["rmm", "Supervision du parc (RMM)"],
  ["details", "Fiche du poste : mises à jour en un clic"],
  ["software", "Installation de logiciels en masse"],
  ["join", "Session d'assistance par code"],
];

const HeroSlider = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % SHOTS.length), 4000);
    return () => clearTimeout(t);
  }, [i]);
  return (
    <div className="rounded-xl border border-slate-700/80 bg-slate-900 shadow-[0_30px_80px_-20px_rgba(56,189,248,0.25)] overflow-hidden" data-testid="hero-slider">
      <div className="flex items-center gap-1.5 px-3 h-7 bg-slate-800/80 border-b border-slate-700/60">
        {["bg-rose-400/70", "bg-amber-400/70", "bg-emerald-400/70"].map((c) => <span key={c} className={`h-2.5 w-2.5 rounded-full ${c}`} />)}
        <span className="ml-3 text-[11px] font-semibold text-slate-300 truncate" data-testid="hero-slider-label">{SHOTS[i][1]}</span>
      </div>
      <div className="relative aspect-video bg-black">
        <AnimatePresence initial={false}>
          <motion.img key={SHOTS[i][0]} src={`/landing/${SHOTS[i][0]}.jpg`} alt={SHOTS[i][1]} className="absolute inset-0 w-full h-full object-cover object-top"
            initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }} />
        </AnimatePresence>
      </div>
      <div className="flex justify-center gap-2 py-2.5 bg-slate-900">
        {SHOTS.map(([k, l], n) => (
          <button key={k} type="button" onClick={() => setI(n)} aria-label={l} data-testid={`hero-slide-${k}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-6 bg-sky-400" : "w-1.5 bg-slate-600 hover:bg-slate-400"}`} />
        ))}
      </div>
    </div>
  );
};

const STATS = [["1 clic", "pour prendre la main"], ["1 500+", "postes testés par serveur"], ["80+", "logiciels à déployer"], ["14 jours", "d'essai gratuit"]];

export const Hero = () => (
  <section className="relative overflow-hidden grid-bg" id="presentation">
    <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center">
      <div className="fade-up">
        <p className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-sky-400 mb-6">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> PRISE EN MAIN + SUPERVISION DU PARC
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] mb-6">
          Dépannez, surveillez <span className="text-sky-400">tous vos PC</span>. Depuis votre navigateur.
        </h1>
        <p className="text-slate-300 text-base md:text-lg max-w-xl mb-9 leading-relaxed">
          ScreenView réunit la prise en main à distance et la supervision de parc (RMM) dans un seul outil, en français, facturé par technicien et non par poste.
        </p>
        <div className="flex flex-wrap gap-3 mb-8">
          <Button asChild size="lg" className="rounded-full px-8 font-semibold transition-transform hover:-translate-y-0.5">
            <Link to="/register" data-testid="hero-register-btn">Essai gratuit 14 jours</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-8 border-slate-700 bg-transparent hover:bg-slate-800 hover:text-white">
            <Link to="/login" data-testid="hero-login-btn">Se connecter</Link>
          </Button>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-400">
          <span className="flex items-center gap-1.5"><Monitor className="h-4 w-4" />Windows</span>
          <span className="flex items-center gap-1.5"><Apple className="h-4 w-4" />macOS</span>
          <span className="flex items-center gap-1.5"><Terminal className="h-4 w-4" />Linux</span>
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Double authentification</span>
        </div>
      </div>
      <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
        <HeroSlider />
        <div className="absolute -bottom-6 -left-4 sm:-left-8 rounded-xl border border-slate-700 bg-[#0F172A]/95 backdrop-blur px-4 py-3 shadow-xl max-w-[260px]" data-testid="hero-help-card">
          <p className="flex items-center gap-2 text-sm font-semibold"><LifeBuoy className="h-4 w-4 text-amber-400" />Aide demandée</p>
          <p className="text-xs text-slate-400 mt-1">PC-ACCUEIL-01 · « L'imprimante ne répond plus »</p>
        </div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 pb-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {STATS.map(([v, l]) => (
        <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4" data-testid={`hero-stat-${v}`}>
          <p className="text-2xl font-extrabold text-white">{v}</p>
          <p className="text-sm text-slate-400">{l}</p>
        </div>
      ))}
    </div>
  </section>
);
